import { useContext, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { AuthContext } from '../contexts/AuthContext'
import { useLocation as useLocationCtx } from '../contexts/LocationContext'
import { listAddresses } from '../services/addressesService'

/**
 * Global guard: a logged-in user with **no saved delivery address** AND
 * **no active delivery location** is sent to `/addresses/new` so they create
 * one before browsing. This fixes the "new user lands on home with nothing
 * set" case — both on web and inside iOS/Android WebViews.
 *
 * Runs *after* RequirePhoneNumber so the phone step always wins first.
 *
 * Renders nothing — side-effect only.
 */
const ALLOWED_PREFIXES = [
  '/addresses', // /addresses, /addresses/new, /addresses/:id/edit
  '/complete-profile',
  '/account-login',
  '/create-account',
  '/otp-verification',
  '/forgot-password',
  '/reset-password',
  '/account', // user can manage profile/addresses tab
  '/terms',
  '/privacy',
  '/contact',
  '/payment/success',
  '/payment/failed',
  // Following, paying for or reading about an order needs no delivery address:
  // a "Track order" link from an email must open the order, not an address form.
  '/order-tracking',
  '/order', // old /order/<id> links redirect to tracking
  '/orders',
  '/order-success',
  '/notifications',
  '/notification-settings'
]

const isAllowed = pathname => ALLOWED_PREFIXES.some(p => pathname === p || pathname.startsWith(p + '/') || pathname.startsWith(p + '?'))

const RequireDeliveryAddress = () => {
  const { user, token } = useContext(AuthContext)
  const { hasLocation, loading: locationLoading } = useLocationCtx() || {}
  const navigate = useNavigate()
  const location = useLocation()

  // Only fetch addresses for logged-in users with a phone (so we don't fight
  // RequirePhoneNumber). Cache aggressively — most users will have ≥1 address.
  const enabled = !!token && !!user && !!user.phone_number
  const { data: addresses, isLoading: addrLoading } = useQuery({
    queryKey: ['addresses'],
    queryFn: async () => {
      const data = await listAddresses()
      return data?.results || []
    },
    enabled,
    staleTime: 2 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    retry: 1
  })

  useEffect(() => {
    if (!enabled) return
    if (locationLoading || addrLoading) return // wait for both checks
    if (isAllowed(location.pathname)) return
    if (hasLocation) return // user already has an active delivery location
    if (addresses && addresses.length > 0) return // has at least one saved

    navigate('/addresses/new?returnTo=' + encodeURIComponent(location.pathname + location.search), {
      replace: true
    })
  }, [enabled, locationLoading, addrLoading, hasLocation, addresses, location.pathname, location.search, navigate])

  return null
}

export default RequireDeliveryAddress
